import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ConversationCampaign, IQuestion } from '@feathr/blackbox';
import { Button, CardV2 as Card, Input, NumberInput } from '@feathr/components'; // eslint-disable-line
import { useLocalUrl } from '@feathr/extender/state';

import * as styles from './QuestionConfig.css';

const addAnswer = (campaign: ConversationCampaign, question: IQuestion) => (): void => {
  const updatedQuestion = {
    ...question,
    answers: [...(question.answers || []), { text: '', uuid: nanoid(12) }],
  };
  campaign.set({ questions: [updatedQuestion] });
};

const removeAnswer =
  (campaign: ConversationCampaign, question: IQuestion, index: number) => (): void => {
    const updatedQuestion = {
      ...question,
      answers: question.answers?.filter((_, i) => i !== index),
    };
    campaign.set({ questions: [updatedQuestion] });
  };

interface IProps {
  readonly campaign: ConversationCampaign;
  readonly question: IQuestion;
}

function QuestionConfig({ campaign, question }: IProps): JSX.Element {
  const localUrl = useLocalUrl();
  const { t } = useTranslation();

  function handleTextChange(newValue?: string): void {
    const updatedQuestion = { ...question, text: newValue ?? '' };
    campaign.set({ questions: [updatedQuestion] });
  }

  function handleResponseChange(newValue?: string): void {
    const updatedQuestion = { ...question, response: newValue ?? '' };
    campaign.set({ questions: [updatedQuestion] });
  }

  function handleDelayChange(newValue?: number): void {
    const updatedQuestion = { ...question, delay: (newValue ?? 0) * 1000 };
    campaign.set({ questions: [updatedQuestion] });
  }

  return (
    <div>
      <Input
        className={styles.row}
        helpText={t('What do you want to ask your audience?')}
        label={t('Question text')}
        onChange={handleTextChange}
        placeholder={t('What part of CookieCon are you most excited about this year?')}
        type={'text'}
        value={question.text}
      />
      <Input
        className={styles.row}
        helpText={t('This will be the text that users see after answering the question.')}
        label={t('Response')}
        onChange={handleResponseChange}
        placeholder={t('Thank you for your response!')}
        type={'text'}
        value={question.response}
      />
      <NumberInput
        className={styles.row}
        helpText={t(
          'How long should Conversations wait before prompting the user with your question?',
        )}
        label={t('Delay (in seconds)')}
        onChange={handleDelayChange}
        value={question.delay ? question.delay / 1000 : 0}
      />

      <Card contentClassName={styles.answersHeader}>
        <Card.Header title={t('Answers')} />
        <Card.Content addVerticalGap={true}>
          {question.answers?.length === 0 && <div>{t('No answers yet.')}</div>}

          {question.answers?.map((answer, index) => {
            function handleAnswerChange(newValue?: string): void {
              const updatedAnswers = question.answers?.map((a, i) =>
                i === index ? { ...a, text: newValue ?? '' } : a,
              );
              const updatedQuestion = { ...question, answers: updatedAnswers };
              campaign.set({ questions: [updatedQuestion] });
            }

            return (
              <div className={styles.answer} key={answer.segment || answer.uuid}>
                <Input
                  label={t('Answer {{num}}', { num: index + 1 })}
                  onChange={handleAnswerChange}
                  type={'text'}
                  value={answer.text}
                  wrapperClassName={styles.answerInput}
                />
                {answer.segment && (
                  <Button
                    link={localUrl(`/data/segments/${answer.segment}`)}
                    target={'_blank'}
                    type={'icon-outlined'}
                  >
                    {t('View group')}
                  </Button>
                )}
                <Button onClick={removeAnswer(campaign, question, index)} type={'icon-outlined'}>
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            );
          })}
        </Card.Content>
        <Card.Actions>
          <Button onClick={addAnswer(campaign, question)}>{t('Add answer')}</Button>
        </Card.Actions>
      </Card>
    </div>
  );
}

export default observer(QuestionConfig);
